import type { Session, SupabaseClient } from '@supabase/supabase-js'
import * as Sentry from '@sentry/nuxt'
import { readTopbarCache, writeTopbarCacheFromUser, clearTopbarCache } from '~/utils/topbarCache'
import { handle401Logout } from '~/utils/handle401Logout'
import type { DepositMethodsConfig } from '~/stores/user'

/**
 * Auth session restoration plugin (client-side only)
 *
 * Returns synchronously so Nuxt can mount Vue immediately.
 * Session restoration runs in the background via restoreSession().
 * Protected routes use `await userStore.waitForAuth()` in middleware.
 *
 * Depends implicitly on the unified Supabase client at
 * `layers/base/app/plugins/supabase.client.ts`. We don't declare `dependsOn`
 * because that plugin has no explicit `name:`, so it isn't a valid target
 * for typed `dependsOn`. Nuxt guarantees layer plugins load before app
 * plugins regardless, which is sufficient ordering for `$supabase` to be
 * injected by the time this plugin's `setup()` runs.
 */
export default defineNuxtPlugin({
  name: 'auth',
  setup() {
    const { $supabase } = useNuxtApp() as { $supabase: SupabaseClient | undefined }
    const userStore = useUserStore()

    // Capture the path synchronously inside setup() — useRoute() must run
    // with a live Nuxt instance, and the async restoreSession() chain runs
    // detached. We only care about the initial path here (used for the
    // /auth/callback short-circuit below); subsequent navigations don't
    // need to re-trigger restoration.
    const initialPath = useRoute().path

    // Skip if Supabase client is not available (misconfigured env).
    if (!$supabase) {
      userStore.setAuthInitialized()
      return
    }

    // /auth/callback short-circuit. callback.vue owns the full OAuth flow:
    // PKCE exchange → /auth-login (which sets aurumrun_session_id) →
    // router.push('/'). Running restoreSession() here would POST
    // /auth-session BEFORE /auth-login had a chance to store the session
    // id, returning 401 reason=no_session_header → the global $fetch 401
    // handler would fire handle401Logout() → supabase.auth.signOut() →
    // wiping the session callback.vue was about to use. Mirrors the
    // pre-migration Privy plugin's short-circuit.
    if (initialPath === '/auth/callback') {
      userStore.setAuthInitialized()
      return
    }

    // Optimistic identity paint: hydrate the topbar (avatar + name) from the
    // last-known identity snapshot BEFORE the async session restore runs, so a
    // returning user never sees the logged-out shell flash. restoreSession()
    // below overwrites this with fresh /auth-session data, or clears it if the
    // session is dead (spec C1/C2/C3). Render-only — no privileged action keys
    // off this (C12). Must stay synchronous, above the detached restore (C8).
    const cached = readTopbarCache()
    if (cached) {
      userStore.setUser({
        id: cached.userId,
        walletAddress: null,
        email: null,
        // Paint the nickname the user actually sees. userStore.displayName
        // prefers `displayName` (the equipped nickname) over `username` (the
        // account handle), and useDisplayNickname falls back to it before the
        // character store loads playerEquipped — so both fields go into their
        // real slots, matching the /auth-session reconcile shape. Render-only
        // (spec C12).
        username: cached.username,
        displayName: cached.displayName,
        avatarUrl: cached.avatarUrl,
        avatarStatus: cached.avatarStatus,
      })
    }

    // Start session restoration in background — DO NOT await
    restoreSession($supabase)
      .catch((error) => {
        console.error('[auth plugin] Session restoration failed:', error)
      })
      .finally(() => {
        userStore.setAuthInitialized()
      })
  },
})

async function restoreSession(supabase: SupabaseClient) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) {
    // No Supabase session (logged out, token expired, browser storage cleared,
    // etc.) but our localStorage may still hold a stale `aurumrun_session_id`
    // from a previous session. Clean it up so it can't be re-sent on subsequent
    // requests as a phantom auth header.
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('aurumrun_session_id')
      } catch {
        // localStorage may be unavailable (private mode, quota); non-fatal
      }
    }
    // The optimistic paint above may have set a user from a stale cache that has
    // no real Supabase session. Drop both so the shell falls back to logged-out
    // (spec C3.2 — this path never hits $fetch, so handle401Logout never runs).
    clearTopbarCache()
    useUserStore().clearUser()
    return
  }

  const balanceStore = useBalanceStore()
  const characterStore = useCharacterStore()
  const oreStore = useOreStore()

  // Email extraction — Supabase exposes a primary email on the user object,
  // populated from whichever identity owns it (email OTP, Google OAuth, …).
  // Fall back to user_metadata.email which some flows write there explicitly.
  const userEmail = extractEmail(session)

  const accessToken = session.access_token

  const config = useRuntimeConfig()
  const { getSessionId } = await import('~/composables/api/useAuthApi')
  let sessionUser: {
    id: string
    walletAddress: string | null
    depositWalletAddress: string | null
    depositWalletSolanaAddress: string | null
    depositWalletTronAddress: string | null
    depositWalletTonAddress: string | null
    tonMemoCode: string | null
    email: string | null
    username: string | null
    displayName: string | null
    avatarUrl: string | null
    avatarStatus: 'active' | 'pending' | 'rejected' | 'removed_by_admin' | null
    avatarUpdatedAt: string | null
    fiatDepositsEnabled: boolean
    bitoloDepositsEnabled: boolean
    speedDepositsEnabled: boolean
  } | null = null

  // Primary: try /auth-session (fast read, Redis session only).
  // Header pattern unchanged: only the TOKEN source moved from Privy to Supabase.
  try {
    const sessionId = getSessionId()
    const result = await $fetch<{
      authenticated: boolean
      session: {
        userId: string
        walletAddress: string | null
        depositWalletAddress: string | null
        depositWalletSolanaAddress: string | null
        depositWalletTronAddress: string | null
        depositWalletTonAddress: string | null
        tonMemoCode: string | null
        email?: string | null
        username?: string | null
        displayName?: string | null
        avatarUrl?: string | null
        avatarStatus?: 'active' | 'pending' | 'rejected' | 'removed_by_admin' | null
        avatarUpdatedAt?: string | null
        fiatDepositsEnabled?: boolean
        bitoloDepositsEnabled?: boolean
        speedDepositsEnabled?: boolean
        depositMethods?: DepositMethodsConfig
      }
    }>(
      `${config.public.apiBase}/auth-session`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(sessionId && { 'X-Session-Id': sessionId }),
        },
      }
    )

    if (result?.authenticated && result?.session) {
      sessionUser = {
        id: result.session.userId,
        walletAddress: result.session.walletAddress,
        depositWalletAddress: result.session.depositWalletAddress,
        depositWalletSolanaAddress: result.session.depositWalletSolanaAddress,
        depositWalletTronAddress: result.session.depositWalletTronAddress,
        depositWalletTonAddress: result.session.depositWalletTonAddress,
        tonMemoCode: result.session.tonMemoCode,
        // /auth-session reads public.users — the same column the withdrawal
        // gate checks — so it is authoritative, INCLUDING when it says null.
        // `userEmail` (off the locally stored Supabase session) can lag the
        // real account: an `email_change` verify that returns no fresh session
        // leaves `session.user.email` null even though auth.users AND
        // public.users already carry the address, which kept the "add email to
        // withdraw" banner up across reloads (issue #787).
        //
        // Keyed on `undefined` rather than on falsiness, so an explicit null
        // still wins. The net effect is simply "the server value always wins":
        // /auth-session has always sent the key, so the userEmail branch is
        // defensive only, not a rollout shim. Treating null as a miss is the
        // thing to avoid — it would make this path disagree with
        // userStore.fetchUser (`response.session.email || null`, no fallback)
        // for any account where auth.users has an email and public.users does
        // not, e.g. after a failed or 409'd /user-link-method, and the banner
        // would flip depending on whether you reloaded.
        email: result.session.email === undefined ? userEmail : result.session.email,
        username: result.session.username ?? null,
        displayName: result.session.displayName ?? null,
        avatarUrl: result.session.avatarUrl ?? null,
        avatarStatus: result.session.avatarStatus ?? null,
        avatarUpdatedAt: result.session.avatarUpdatedAt ?? null,
        fiatDepositsEnabled: result.session.fiatDepositsEnabled ?? false,
        bitoloDepositsEnabled: result.session.bitoloDepositsEnabled ?? false,
        speedDepositsEnabled: result.session.speedDepositsEnabled ?? false,
        depositMethods: result.session.depositMethods,
      }
    }
  } catch (err) {
    // /auth-session failed (typically 401 from a stale or missing session).
    //
    // Important: do NOT silently re-login here. The backend's /auth-session
    // endpoint refreshes the session when it succeeds (Step 4 of the
    // session-refresh-fastgate plan), so a failure here means the session
    // is genuinely dead — masking it with a `/auth-login` retry would let
    // a logged-out user appear logged in.
    //
    // Our custom $fetch wrapper (apps/web/app/utils/$fetch.ts) has already
    // intercepted any 401 from this call and triggered handle401Logout()
    // — which clears stores, removes localStorage.aurumrun_session_id, the
    // topbar cache, and opens the login modal. We just leave sessionUser=null.
    const statusCode = (err as { statusCode?: number }).statusCode
    if (statusCode === 403) {
      // Banned: the global $fetch interceptor only acts on 401, so a 403 never
      // triggers handle401Logout. Clear the optimistic paint + cache and drop to
      // the guest/login state ourselves (spec C3.3, default 403 = guest+login).
      clearTopbarCache()
      handle401Logout()
    }
    // Other errors (transient network / 5xx) intentionally keep the optimistic
    // paint + cache (spec C4) — a flaky network must not log the user out.
    console.warn('[auth plugin] /auth-session failed — leaving user logged out')
  }

  if (sessionUser) {
    const userStore = useUserStore()
    userStore.setUser(sessionUser)
    // Refresh the optimistic cache from fresh /auth-session data. sessionUser
    // carries id, username, and the SERVER-GATED avatarUrl/avatarStatus — exactly
    // the cache shape (spec C1). Full replace, so an active→removed transition is
    // corrected on the next load (C2).
    writeTopbarCacheFromUser(sessionUser)
    Sentry.setUser({ id: sessionUser.id })
    await balanceStore.fetchBalance(accessToken)
    // Non-blocking: cosmetics and ore are visual-only
    characterStore.fetchInventory().catch(() => {})
    oreStore.fetchOreData(accessToken).catch(() => {})

    // Push the access token into the realtime client BEFORE subscribing
    // to any private:true channel. supabase-js@2.90's _handleTokenChanged
    // skips INITIAL_SESSION (the event fired on page-reload rehydration),
    // so without this the channel would phx_join with a null access_token
    // and the realtime server would reject it via the
    // messages_owner_topics_select RLS policy. supabase.client.ts also
    // listens for INITIAL_SESSION as a fire-and-forget fallback; this
    // explicit await guarantees strict ordering against the subscribes
    // below.
    await supabase.realtime.setAuth(accessToken)

    // Subscribe to real-time deposit and balance updates (broadcast channels)
    const { subscribe: subscribeToBalance } = useBalanceRealtime()
    const { subscribe: subscribeToDeposits } = useDepositRealtime()
    subscribeToBalance(sessionUser.id)
    subscribeToDeposits(sessionUser.id)
  }
}

/**
 * Pull the primary email off a Supabase session.
 *
 * Supabase's `user.email` is the canonical primary — populated by email OTP
 * directly, and by Google OAuth on first sign-in. `user_metadata.email` is a
 * fallback some flows (e.g. OAuth providers that don't auto-confirm email)
 * write explicitly. Wallet-only users have neither, hence the nullable return.
 */
function extractEmail(session: Session): string | null {
  const direct = session.user.email
  if (direct) return direct
  const meta = session.user.user_metadata as { email?: unknown } | null | undefined
  const metaEmail = meta?.email
  return typeof metaEmail === 'string' && metaEmail.length > 0 ? metaEmail : null
}
